/* Copyright (c) 2020-present CloudIO Inc */
import React, { useState } from 'react';
import {
  MenuItem,
  TextField,
  Typography,
  Divider,
  Theme,
  TextFieldProps,
} from '@mui/material';
import Icon from '../icons/Icon';
import { BaseComponentItem, getManager } from '../componentregistry';
import { makeStyles } from '@mui/styles';
import { DateInputWithTZ } from 'custom/DateInputWithTZ';
import { format, parseISO } from 'date-fns';
import { useUserDateFormat, useUserDateTimeFormat } from 'root/useUser';
import clsx from 'clsx';
import zIndex from 'constants/ZIndex';

const useStyles = makeStyles((theme: Theme) => ({
  menuItem: {
    minWidth: 180,
  },
  icon: {
    marginRight: 12,
  },
  inputContainer: {
    padding: '8px 16px',
    minWidth: 250,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  header: {
    padding: '8px 16px',
    fontWeight: 'bold',
  },
  popper: {
    zIndex: zIndex.AutoCompletePopper,
    '& .MuiDateTimePickerTabs-tabs': {
      color: '#fff',
    },
  },
}));

type Props = {
  itemId: string;
  item: BaseComponentItem | undefined;
  platform: any;
  rowIndex: number;
  onClose: () => void;
};

const getComponentCategory = (type: string = '') => {
  switch (type) {
    case 'Button':
    case 'Chip':
    case 'ActionButton':
    case 'Icon':
    case 'IconButton':
    case 'IconSwitch':
    case 'FAB':
    case 'SpeedDial':
    case 'SpeedDialAction':
    case 'MenuItem':
    case 'Box':
    case 'TabItem':
      return 'action';
    case 'NumberField':
    case 'NumberColumn':
    case 'SearchNumberField':
      return 'number';
    case 'DateField':
    case 'DateColumn':
    case 'SearchDateField':
      return 'date';
    case 'Checkbox':
    case 'CheckboxColumn':
    case 'RadioBox':
    case 'SearchCheckbox':
    case 'RowSelectionColumn':
      return 'boolean';
    case 'TextField':
    case 'InputBase':
    case 'SearchTextField':
    case 'StringColumn':
    case 'Select':
    case 'AsyncSelect':
    case 'SelectColumn':
    case 'LOVColumn':
    case 'LOVField':
    case 'LookupSelect':
    case 'SearchLookupField':
    case 'SearchSelectField':
    default:
      return 'text';
  }
};

const isColumn = (type: string = '') => type.includes('Column');

export default function TestAssertionMenu({
  itemId,
  item,
  platform,
  rowIndex,
  onClose,
}: Props): React.ReactElement | null {
  const classes = useStyles();
  const [value, setValue] = useState('');
  const [showInput, setShowInput] = useState(false);
  const [inputType, setInputType] = useState<'text' | 'date'>('text');
  const dateFormat = useUserDateFormat();
  const dateTimeFormat = useUserDateTimeFormat();
  const itemType = item?.type;
  const category = getComponentCategory(itemType);
  const isCol = isColumn(itemType);
  const name = item ? getManager(item.type).name : 'Element';
  const hasExpected = value.trim().length > 0;
  const itemProps = (item as any)?.props || {};
  const showTime = itemProps.showTime;
  const attr = itemProps.attr;
  const addAssertion = (code: string) => {
    if (platform.isRecording()) {
      platform.recordLine('', code);
    }
    onClose();
  };

  const handleInputClick = (type: 'text' | 'date') => {
    setInputType(type);
    setShowInput(true);
  };

  const formatDate = (
    dateVal: Date | string,
    fmt?: string,
    isShowTime?: boolean,
  ) => {
    if (typeof value === 'string') {
      dateVal = parseISO(value);
    }
    if (!(dateVal instanceof Date)) {
      throw new Error(
        'Value to be typed into a date field must be a ISO date string or a Date object!',
      );
    }
    if (!fmt) {
      fmt = isShowTime ? dateTimeFormat : dateFormat;
    }
    return format(dateVal, fmt);
  };

  const getAccessor = () => {
    const opts = isCol ? `, { row: ${rowIndex} }` : '';
    return `await ui.getInputText('${itemId}'${opts})`;
  };

  const renderInput = () => {
    if (!showInput) return null;
    return (
      <div
        className={classes.inputContainer}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        role="tab"
        data-metadata="cloudio"
      >
        {inputType === 'date' ? (
          <DateInputWithTZ
            value={value || null}
            onChange={(date: Date | null) => {
              setValue(date ? date.toISOString() : '');
            }}
            showTime={!!showTime}
            attr={attr}
            hideTodayButton={false}
            PopperProps={{
              className: clsx(classes.popper, 'custom-datepicker-dialog'),
            }}
            renderInput={(params: TextFieldProps) => (
              <TextField
                {...params}
                variant="standard"
                fullWidth
                label="Expected Date"
                inputProps={{
                  ...params.inputProps,
                }}
              />
            )}
          />
        ) : (
          <TextField
            autoFocus
            fullWidth
            label="Expected Value"
            type={category === 'number' ? 'number' : 'text'}
            placeholder="Expected Value"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          />
        )}
      </div>
    );
  };

  const renderActionAssertions = () => (
    <>
      {!showInput && (
        <MenuItem
          onClick={() => handleInputClick('text')}
          className={classes.menuItem}
        >
          <Icon iconName="edit" className={classes.icon} /> Set Expected Value
        </MenuItem>
      )}
      <MenuItem
        disabled={!hasExpected}
        onClick={() => {
          if (!showInput) {
            handleInputClick('text');
            return;
          }
          addAssertion(
            `expect(await ui.text({ testid: '${itemId}' })).to.eq('${value}')`,
          );
        }}
        className={classes.menuItem}
      >
        <Icon iconName="text" className={classes.icon} /> Assert Button Label
      </MenuItem>
      <MenuItem
        onClick={() =>
          addAssertion(
            `expect(await ui.isDisabled({ testid: '${itemId}' })).to.be.false`,
          )
        }
        className={classes.menuItem}
      >
        <Icon iconName="play-circle" className={classes.icon} /> Assert Button
        Enabled
      </MenuItem>
      <MenuItem
        onClick={() =>
          addAssertion(
            `expect(await ui.isDisabled({ testid: '${itemId}' })).to.be.true`,
          )
        }
        className={classes.menuItem}
      >
        <Icon iconName="stop-circle" className={classes.icon} /> Assert Button
        Disabled
      </MenuItem>
    </>
  );

  const renderTextAssertions = () => (
    <>
      {!showInput && (
        <MenuItem
          onClick={() => handleInputClick('text')}
          className={classes.menuItem}
        >
          <Icon iconName="edit" className={classes.icon} /> Set Expected Value
        </MenuItem>
      )}
      <MenuItem
        disabled={!hasExpected}
        onClick={() =>
          addAssertion(`expect(${getAccessor()}).to.eq('${value}')`)
        }
        className={classes.menuItem}
      >
        <Icon iconName="equals" className={classes.icon} /> Assert Equals
      </MenuItem>
      <MenuItem
        disabled={!hasExpected}
        onClick={() =>
          addAssertion(`expect(${getAccessor()}).to.include('${value}')`)
        }
        className={classes.menuItem}
      >
        <Icon iconName="brackets" className={classes.icon} /> Assert Contains
      </MenuItem>
      <MenuItem
        disabled={!hasExpected}
        onClick={() =>
          addAssertion(
            `expect((${getAccessor()}).startsWith('${value}')).to.be.true`,
          )
        }
        className={classes.menuItem}
      >
        <Icon iconName="angle-double-right" className={classes.icon} /> Assert
        Starts With
      </MenuItem>

      <MenuItem
        disabled={!hasExpected}
        onClick={() =>
          addAssertion(`expect((${getAccessor()})).to.have.lengthOf(${value})`)
        }
        className={classes.menuItem}
      >
        <Icon iconName="check-circle" className={classes.icon} /> Assert Length
      </MenuItem>
      <MenuItem
        disabled={!hasExpected}
        onClick={() =>
          addAssertion(`expect((${getAccessor()})).to.match(${value})`)
        }
        className={classes.menuItem}
      >
        <Icon iconName="check-circle" className={classes.icon} /> Assert Match
        (Regex)
      </MenuItem>
      <MenuItem
        onClick={() =>
          addAssertion(`expect((${getAccessor()})).to.not.be.empty`)
        }
        className={classes.menuItem}
      >
        <Icon iconName="empty-set" className={classes.icon} /> Assert Not Empty
      </MenuItem>
    </>
  );

  const renderNumberAssertions = () => (
    <>
      {!showInput && (
        <MenuItem
          onClick={() => handleInputClick('text')}
          className={classes.menuItem}
        >
          <Icon iconName="edit" className={classes.icon} /> Set Expected Number
        </MenuItem>
      )}
      <MenuItem
        disabled={!hasExpected}
        onClick={() =>
          addAssertion(`expect(Number(${getAccessor()})).to.eq(${value})`)
        }
        className={classes.menuItem}
      >
        <Icon iconName="equals" className={classes.icon} /> Assert Equals
      </MenuItem>
      <MenuItem
        disabled={!hasExpected}
        onClick={() =>
          addAssertion(`expect(Number(${getAccessor()})).to.be.above(${value})`)
        }
        className={classes.menuItem}
      >
        <Icon iconName="greater-than" className={classes.icon} /> Assert Greater
        Than
      </MenuItem>
      <MenuItem
        disabled={!hasExpected}
        onClick={() =>
          addAssertion(`expect(Number(${getAccessor()})).to.be.below(${value})`)
        }
        className={classes.menuItem}
      >
        <Icon iconName="less-than" className={classes.icon} /> Assert Less Than
      </MenuItem>
    </>
  );

  const renderDateAssertions = () => (
    <>
      {!showInput && (
        <MenuItem
          onClick={() => handleInputClick('date')}
          className={classes.menuItem}
        >
          <Icon iconName="calendar" className={classes.icon} /> Set Expected
          Date
        </MenuItem>
      )}

      <MenuItem
        disabled={!hasExpected}
        onClick={() => {
          const formattedValue = formatDate(value, itemProps.format, showTime);
          addAssertion(`expect(${getAccessor()}).to.eq('${formattedValue}')`);
        }}
        className={classes.menuItem}
      >
        <Icon iconName="equals" className={classes.icon} /> Assert Date Equals
      </MenuItem>

      <MenuItem
        disabled={!hasExpected}
        onClick={() => {
          const formattedValue = formatDate(value, itemProps.format, showTime);
          addAssertion(
            `expect(new Date(${getAccessor()})).to.be.below(new Date('${formattedValue}'))`,
          );
        }}
        className={classes.menuItem}
      >
        <Icon iconName="greater-than" className={classes.icon} /> Assert Date
        Before
      </MenuItem>

      <MenuItem
        disabled={!hasExpected}
        onClick={() => {
          const formattedValue = formatDate(value, itemProps.format, showTime);
          addAssertion(
            `expect(new Date(${getAccessor()})).to.be.above(new Date('${formattedValue}'))`,
          );
        }}
        className={classes.menuItem}
      >
        <Icon iconName="less-than" className={classes.icon} /> Assert Date After
      </MenuItem>
    </>
  );

  const renderBooleanAssertions = (isRadioBox: boolean) => (
    <>
      {!showInput && isRadioBox && (
        <MenuItem
          onClick={() => handleInputClick('text')}
          className={classes.menuItem}
        >
          <Icon iconName="edit" className={classes.icon} /> Set Expected Option
        </MenuItem>
      )}
      <MenuItem
        disabled={!hasExpected && isRadioBox}
        onClick={() => {
          let options = '';
          if (isRadioBox) {
            options = `, { value: '${value}'${
              isCol ? `, row: ${rowIndex}` : ''
            } }`;
          } else if (isCol) {
            options = `, { row: ${rowIndex} }`;
          }

          addAssertion(
            `expect(await ui.isChecked('${itemId}'${options})).to.be.true`,
          );
        }}
        className={classes.menuItem}
      >
        <Icon iconName="square-check" className={classes.icon} /> Assert Checked
      </MenuItem>
      <MenuItem
        disabled={!hasExpected && isRadioBox}
        onClick={() => {
          let options = '';
          if (isRadioBox) {
            options = `, { value: '${value}'${
              isCol ? `, row: ${rowIndex}` : ''
            } }`;
          } else if (isCol) {
            options = `, { row: ${rowIndex} }`;
          }
          addAssertion(
            `expect(await ui.isChecked('${itemId}'${options})).to.be.false`,
          );
        }}
        className={classes.menuItem}
      >
        <Icon iconName="square" className={classes.icon} /> Assert Unchecked
      </MenuItem>
    </>
  );

  const renderAssertions = () => {
    switch (category) {
      case 'action':
        return renderActionAssertions();
      case 'number':
        return renderNumberAssertions();
      case 'date':
        return renderDateAssertions();
      case 'boolean':
        return renderBooleanAssertions(itemType === 'RadioBox');
      case 'text':
        return renderTextAssertions();
      default:
        return null;
    }
  };

  return (
    <>
      <Typography variant="subtitle2" className={classes.header}>
        Add Assertion for {name}
      </Typography>
      <Divider />
      {renderInput()}
      {renderAssertions()}
      <Divider />
      <MenuItem
        onClick={() =>
          addAssertion(
            `expect(await ui.exists({ testid: '${itemId}'${
              isCol ? `, row: ${rowIndex}` : ''
            } })).to.be.true`,
          )
        }
        className={classes.menuItem}
      >
        <Icon iconName="eye" className={classes.icon} /> Assert Exists
      </MenuItem>
    </>
  );
}
